import type { WorkItem } from "@/core/api/services";
import { Badge } from "@hub/ui/components/ui/badge";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import { useWorkItemActions } from "../api/useWorkItemActions";
import { Button } from "@hub/ui/components/ui/button";
import { statusColors } from "@/core/constants/statusColors";

interface QueueItemProps {
  item: WorkItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

function formatAge(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "< 1h";
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
}

export function QueueItem({ item, isSelected, onSelect }: QueueItemProps) {
  const { claim, claiming } = useWorkItemActions();

  const handleClaim = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await claim(item.id);
      onSelect(item.id);
    } catch {
      // Swallowed: prevents unhandled rejection; user sees no state change
    }
  };

  const age = item.createdAt ? formatAge(timestampDate(item.createdAt)) : "";
  const patientName = item.context["patient_name"] ?? "";
  const sourceRef = item.sourceRef || item.id.slice(0, 8);

  return (
    <button
      onClick={() => onSelect(item.id)}
      className={`
        w-full text-left px-3 py-2.5 border-l-[3px] transition-colors
        ${
          isSelected
            ? "border-l-coronis-blue-600 bg-coronis-blue-50 dark:bg-coronis-blue-900/20"
            : "border-l-transparent hover:bg-gray-50 dark:hover:bg-white/5"
        }
      `}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {item.type}
            </span>
            <span className="text-xs text-gray-400">{sourceRef}</span>
          </div>
          {patientName && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {patientName}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={`text-xs ${statusColors[item.status] ?? statusColors.open}`}>
            {item.status.replace(/_/g, " ")}
          </Badge>
          <span className="text-xs text-gray-400">{age}</span>
        </div>
      </div>

      {!item.claimedBy && (
        <div className="mt-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs"
            onClick={handleClaim}
            disabled={claiming}
          >
            Claim
          </Button>
        </div>
      )}
    </button>
  );
}
